import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FooterActions,
  FormGroup,
  InputLabel,
  TextInput,
  ErrorText,
  ModalCancelButton,
  ModalSubmitButton,
} from './CreateUserModal.styles';
import { useCreateUser } from '../hooks/useUsersAdmin';

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

const CreateUserModal = ({ isOpen, onClose, clientId }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});

  const createUserMutation = useCreateUser({
    onSuccess: () => {
      resetState();
      onClose?.();
    },
    onError: (err) => {
      window.alert(`Failed to add user${err?.message ? `: ${err.message}` : '.'}`);
    },
  });

  const resetState = () => {
    setUsername('');
    setEmail('');
    setErrors({});
  };

  const handleClose = () => {
    if (createUserMutation.isPending) return;
    resetState();
    onClose?.();
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, createUserMutation.isPending]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();

    const nextErrors = {};
    if (!username.trim()) nextErrors.username = 'Name is required';
    if (!email.trim()) nextErrors.email = 'Email is required';
    else if (!EMAIL_PATTERN.test(email.trim())) nextErrors.email = 'Enter a valid email address';

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (!clientId) {
      window.alert('Missing client context — cannot add a user without a client.');
      return;
    }

    createUserMutation.mutate({
      email: email.trim(),
      clientId,
      username: username.trim(),
    });
  };

  return createPortal(
    <ModalOverlay role="presentation">
      <ModalContent
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-user-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle id="create-user-modal-title">Add user</ModalTitle>
          <ModalCloseButton type="button" onClick={handleClose} aria-label="Close dialog">
            <X size={18} />
          </ModalCloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <InputLabel htmlFor="userName">Name</InputLabel>
              <TextInput
                id="userName"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Jane Doe"
                $hasError={!!errors.username}
                aria-describedby={errors.username ? 'user-name-error' : undefined}
                autoFocus
              />
              {errors.username && (
                <ErrorText id="user-name-error" role="alert">
                  {errors.username}
                </ErrorText>
              )}
            </FormGroup>

            <FormGroup>
              <InputLabel htmlFor="userEmail">Email</InputLabel>
              <TextInput
                id="userEmail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@example.com"
                $hasError={!!errors.email}
                aria-describedby={errors.email ? 'user-email-error' : undefined}
              />
              {errors.email && (
                <ErrorText id="user-email-error" role="alert">
                  {errors.email}
                </ErrorText>
              )}
            </FormGroup>
          </ModalBody>

          <ModalFooter>
            <FooterActions>
              <ModalCancelButton
                type="button"
                onClick={handleClose}
                disabled={createUserMutation.isPending}
              >
                Cancel
              </ModalCancelButton>
              <ModalSubmitButton type="submit" disabled={createUserMutation.isPending}>
                {createUserMutation.isPending && (
                  <Loader2 style={{ animation: 'spin 1s linear infinite' }} />
                )}
                {createUserMutation.isPending ? 'Adding…' : 'Add user'}
              </ModalSubmitButton>
            </FooterActions>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default CreateUserModal;
